import React from "react";
import { Form, Input, Select } from "antd";
import {
    SupplierTypeGroup,
    SUPPLIER_TYPE_LABELS,
    EVALUATION_GOAL_LABELS,
    BuyerBriefIntake,
} from "../../../types/BuyerBriefTypes";
import { CONDITIONAL_TOPICS } from "../topicConfig";

const BUDGET_OPTIONS = [
    { value: "under_50k", label: "Under $50K" },
    { value: "50k_250k", label: "$50K - $250K" },
    { value: "250k_1m", label: "$250K - $1M" },
    { value: "1m_5m", label: "$1M - $5M" },
    { value: "over_5m", label: "Over $5M" },
];

interface Props {
    intake: Partial<BuyerBriefIntake>;
    onChange: (updates: Partial<BuyerBriefIntake>) => void;
}

const SupplierIdentificationStep: React.FC<Props> = ({ intake, onChange }) => {
    return (
        <div>
            <h3>Supplier Identification</h3>
            <Form layout="vertical">
                <Form.Item label="Supplier Name" required>
                    <Input
                        placeholder="e.g. Acme Corporation"
                        value={intake.supplierName}
                        onChange={(e) => onChange({ supplierName: e.target.value })}
                    />
                </Form.Item>
                <Form.Item label="Supplier Website">
                    <Input
                        placeholder="e.g. https://www.acme.com"
                        value={intake.supplierWebsite}
                        onChange={(e) => onChange({ supplierWebsite: e.target.value })}
                    />
                </Form.Item>
                <Form.Item label="Supplier Type" required>
                    <Select
                        placeholder="Select supplier type"
                        value={intake.supplierType}
                        onChange={(val) => {
                            // Only clear conditional topic IDs for the old supplier type; preserve universal selections
                            const oldType = intake.supplierType as SupplierTypeGroup | undefined;
                            const oldConditionalIds = new Set(
                                oldType ? (CONDITIONAL_TOPICS[oldType] || []).map((t) => t.id) : []
                            );
                            const preserved = (intake.selectedTopics || []).filter(
                                (topicId) => !oldConditionalIds.has(topicId)
                            );
                            const newConditionalTopicIds = (CONDITIONAL_TOPICS[val] || []).map(
                                (t) => t.id
                            );
                            onChange({
                                supplierType: val,
                                selectedTopics: preserved.concat(newConditionalTopicIds),
                            });
                        }}
                    >
                        {Object.entries(SUPPLIER_TYPE_LABELS).map(([key, label]) => (
                            <Select.Option key={key} value={key}>
                                {label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Evaluation Goal" required>
                    <Select
                        placeholder="Select evaluation goal"
                        value={intake.evaluationGoal}
                        onChange={(val) => onChange({ evaluationGoal: val })}
                    >
                        {Object.entries(EVALUATION_GOAL_LABELS).map(([key, label]) => (
                            <Select.Option key={key} value={key}>
                                {label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Budget Range">
                    <Select
                        placeholder="Select budget range (optional)"
                        value={intake.budgetRange}
                        onChange={(val) => onChange({ budgetRange: val })}
                        allowClear
                    >
                        {BUDGET_OPTIONS.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>
                                {opt.label}
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>
                <Form.Item label="Customer Context">
                    <Input.TextArea
                        placeholder="Describe your relationship with this supplier or any relevant context (optional)"
                        value={intake.customerContext}
                        onChange={(e) => onChange({ customerContext: e.target.value })}
                        rows={3}
                    />
                </Form.Item>
                <Form.Item label="Priority Topic">
                    <Input
                        placeholder="e.g. Data security posture (optional)"
                        value={intake.priorityTopic}
                        onChange={(e) => onChange({ priorityTopic: e.target.value })}
                    />
                </Form.Item>
            </Form>
        </div>
    );
};

export default SupplierIdentificationStep;
